import type { ReviewComplianceEvaluationForm } from "@/forms/compliance/compliance.form";
import { useForm } from "@tanstack/react-form";
import { useAuthStore } from "@/stores/authStore";
import { useGetEvaluationComments, useRespondToEvaluation } from "@/hooks/compliance/compliance.hook";
import PencomTextField from "@/components/input/PencomTextField";
import PencomFileUpload from "@/components/input/PencomFileUpload";
import { useState, useRef } from "react";
import { useFileUpload } from "@/hooks/file-upload/file-upload.hook";
import { EStorage } from "@/constants/enums/storage.enum";
import { PrimaryButton } from "@/components/buttons/PrimaryButton";
import { toast } from "@/utils/toast";
import { useLocation, useNavigate } from "react-router-dom";
import { AxiosError } from "axios";
import CommentsTrail from "@/components/pcc/CommentsTrail";

const ReviewComplianceEvaluation = () => {
    const { currentEmployer } = useAuthStore();
    const { state } = useLocation();
    const evaluationId = state.evaluationId;
    const { mutate: reviewComplianceEvaluation, isPending: isReviewingComplianceEvaluation } = useRespondToEvaluation();
    const { data: evaluationComments } = useGetEvaluationComments(evaluationId || '');
    const { mutate: uploadFile, isPending: isUploadingFiles } = useFileUpload();
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [documentUrls, setDocumentUrls] = useState<string[]>([]);
    const [uploadingFileIndex, setUploadingFileIndex] = useState<number | null>(null);
    const fileUrlMapRef = useRef<Map<string, string>>(new Map());
    const navigate = useNavigate();
    const getFileKey = (file: File) => `${file.name}-${file.size}-${file.lastModified}`;

    const handleFilesSelect = (files: File[]) => {
        setSelectedFiles(files);

        const newFiles = files.filter((file) => {
            const fileKey = getFileKey(file);
            return !fileUrlMapRef.current.has(fileKey);
        });

        if (newFiles.length === 0) {
            const updatedUrls = files.map((f) => {
                const key = getFileKey(f);
                return fileUrlMapRef.current.get(key) || "";
            }).filter((url) => url !== "");
            setDocumentUrls(updatedUrls);
            return;
        }


        setUploadingFileIndex(0);
        uploadFile({
            file: newFiles.length === 1 ? newFiles[0] : newFiles,
            path: EStorage.COMPLIANCE,
            document_type: EStorage.COMPLIANCE,
        }, {
            onSuccess: (response: { data?: { urls?: string[]; url?: string }; urls?: string[]; url?: string } | string[]) => {
                toast.success('File(s) Uploaded Succesfully')
                let urls: string[] = [];

                if (newFiles.length === 1) {
                    const url = Array.isArray(response)
                        ? response[0]
                        : response?.url
                            ? response.url
                            : response?.data?.url
                                ? response.data.url
                                : "";
                    if (url) urls = [url];
                } else {
                    urls = Array.isArray(response)
                        ? response
                        : response?.urls
                            ? response.urls
                            : response?.data?.urls
                                ? response.data.urls
                                : [];
                }

                newFiles.forEach((file, index) => {
                    const fileKey = getFileKey(file);
                    if (urls[index]) {
                        fileUrlMapRef.current.set(fileKey, urls[index]);
                    }
                });

                const updatedUrls = files.map((f) => {
                    const key = getFileKey(f);
                    return fileUrlMapRef.current.get(key) || "";
                }).filter((url) => url !== "");

                setDocumentUrls(updatedUrls);
                setUploadingFileIndex(null);
            },
            onError: (error) => {
                console.error(error);
                setUploadingFileIndex(null);
                newFiles.forEach((file) => {
                    const fileKey = getFileKey(file);
                    fileUrlMapRef.current.delete(fileKey);
                    setSelectedFiles((prev) => prev.filter((f) => getFileKey(f) !== fileKey));
                });
            }
        });
    };

    const handleFileRemoveAt = (index: number) => {
        const fileToRemove = selectedFiles[index];
        if (fileToRemove) {
            const fileKey = getFileKey(fileToRemove);
            fileUrlMapRef.current.delete(fileKey);
        }

        setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
        setDocumentUrls((prev) => prev.filter((_, i) => i !== index));
    };

    const form = useForm({
        defaultValues: {
            evaluationId: evaluationId,
            comment: '',
            documents: [],
            employerId: currentEmployer?.employerId || '',
            employerName: currentEmployer?.companyName || '',
        } as ReviewComplianceEvaluationForm,
        onSubmit: async ({ value }) => {
            reviewComplianceEvaluation({
                evaluationId: value.evaluationId,
                comment: value.comment,
                documentUrls: documentUrls,
                employerId: value.employerId,
                employerName: value.employerName,
            }, {
                onSuccess: () => {
                    toast.success('Compliance evaluation reviewed successfully');
                    navigate(-1);
                },
                onError: (error) => {
                    const err = error as AxiosError<{ message?: string }>;
                    toast.error(err.response?.data?.message || 'Failed to review compliance evaluation');
                }
            });
        },
    });
    return <div className="w-full flex flex-col min-h-screen">
        <div className="w-full flex flex-col  max-w-[635px] mx-auto mt-6">
            <div className="flex flex-col gap-y-4 items-center">
                <p className="font-semibold text-2xl font-clash-display text-medium-dark-grey">Compliance Issues Found</p>
                <p className="font-regular font-clash-display text-sm text-medium-dark-grey">We found issues that require clarifications. Please attach supporting documents below</p>
            </div>
            <div className='flex flex-col mt-8 gap-y-6'>
                {evaluationId && evaluationComments && (
                    <CommentsTrail comments={evaluationComments} maxVisible={3} />
                )}

                <PencomFileUpload
                    id="compliance-evaluation-upload"
                    label="Compliance Evaluation"
                    placeholder="Click here or drag and drop to upload "
                    accept=".pdf,.jpg,.jpeg,.png"
                    maxSize={10}
                    maxFiles={5}
                    onFilesSelect={handleFilesSelect}
                    selectedFiles={selectedFiles}
                    hideFileList
                    className=""
                    disabled={isUploadingFiles || uploadingFileIndex !== null}
                />

                {selectedFiles.length > 0 && (
                    <div className="flex flex-col gap-y-3">
                        <div className="flex flex-row gap-x-3 gap-y-3 flex-wrap">
                            {selectedFiles.map((file, index) => (
                                <div
                                    key={`${file.name}-${index}`}
                                    className="flex items-center justify-between bg-baby-blue border border-navy-blue rounded-lg px-4 py-2.5 gap-x-3 flex-1 min-w-[200px] max-w-[calc(50%-6px)]"
                                >
                                    <div className="flex items-center gap-x-2 flex-1 min-w-0">
                                        <img
                                            src="/assets/icons/document-solid.svg"
                                            alt="document icon"
                                            className="w-4 h-4 flex-shrink-0"
                                        />
                                        <p className="font-clash-display text-xs text-medium-dark-grey font-medium truncate">
                                            {file.name}
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => handleFileRemoveAt(index)}
                                        disabled={isUploadingFiles || uploadingFileIndex !== null}
                                        className="flex items-center justify-center w-5 h-5 flex-shrink-0 hover:opacity-70 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                                        title="Remove file"
                                    >
                                        <img
                                            src="/assets/icons/delete.svg"
                                            alt="delete"
                                            className="w-4 h-4"
                                        />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <form.Field name="comment"
                    validators={{
                        onChange: ({ value }) => {
                            if (!value) return 'Comment is required.';
                            if (value.length < 10) return 'Comment must be at least 10 characters long.';
                            return undefined;
                        },
                    }}
                    children={(field) => {
                        const wordCount = field.state.value.trim() === ''
                            ? 0
                            : field.state.value.trim().split(/\s+/).filter(word => word.length > 0).length;

                        return (
                            <div className="flex-1">
                                <PencomTextField
                                    id="comment"
                                    label="Comment"
                                    placeholder="Enter comment"
                                    value={field.state.value}
                                    onTextareaChange={(value) => field.handleChange(value.target.value)}
                                    type="text"
                                    multiline={true}
                                    rows={4}
                                    className="min-h-[100px]"
                                />
                                <div className="flex justify-between items-center mt-1">
                                    {field.state.meta.errors.length > 0 ? (
                                        <p className="text-red-500 text-xs">
                                            {field.state.meta.errors[0]}
                                        </p>
                                    ) : (
                                        <div />
                                    )}
                                    <p className="text-medium-dark-grey text-xs font-clash-display">
                                        {wordCount} {wordCount === 1 ? 'word' : 'words'}
                                    </p>
                                </div>
                            </div>
                        );
                    }}
                />

                <form.Subscribe
                    children={(state) => {
                        const isDisabled = !state.values.comment || state.values.comment.length < 10 || isReviewingComplianceEvaluation || isUploadingFiles || uploadingFileIndex !== null;
                        const title = uploadingFileIndex !== null
                            ? "Uploading Files..."
                            : isReviewingComplianceEvaluation
                                ? "Submitting..."
                                : "Submit to PenCom";
                        return (
                            <PrimaryButton className="mt-10" title={title}
                                onClick={() => form.handleSubmit()}
                                disabled={isDisabled}
                                isLoading={isReviewingComplianceEvaluation || isUploadingFiles}
                            />
                        )
                    }}
                />


            </div>


        </div>

    </div>
};

export default ReviewComplianceEvaluation;